/**
 * The real screen for a collection, where one exists.
 *
 * Not a lookalike: this is the component the side menu routes to. Opening
 * `unit_master` in the chart mounts the same `UnitsList` you get from
 * Organisation → Units, with its own toolbar, its own filter dropdowns, its own
 * column set and its own actions. Nothing about it is reimplemented here, so
 * nothing about it can drift out of step.
 *
 * Each one takes `appId`, which is the whole trick. The screens already scope
 * themselves through a `selectedProjectId` that their Project dropdown writes;
 * `appId` just seeds it, and the backend intersects it with the caller's own
 * scope exactly as it does when the dropdown is used by hand. So the screen is
 * pointed at the branch's project without a single permission check moving.
 *
 * With `appId` set, those screens also drop the Project column and the Project
 * dropdown (both answer a question the chart has already asked) and hide
 * Create — a record created from here would be filed under the SESSION's
 * project, not the one on screen, which is a bug rather than a feature.
 *
 * Lazily imported, every one of them. These are large components with their own
 * services and value sets; bundling two dozen of them into the explorer's chunk
 * would make a screen that mostly draws boxes the heaviest route in the app.
 * Nothing is fetched or parsed until a collection is actually opened.
 *
 * A collection that is absent here is not broken — it falls back to the
 * explorer's own grid, which reads any project-scoped collection generically.
 */
import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

export type ScreenComponent = ComponentType<{ embedded?: boolean; appId?: string }>;

// Partial, because a lookup that misses is the normal case — most collections
// have no screen, and the caller must see `undefined` to fall back.
export const COLLECTION_SCREENS:
  Partial<Record<string, LazyExoticComponent<ScreenComponent>>> = {
  agent_master: lazy(() => import('../agents/agent-list')),
  ai_model_master: lazy(() => import('../ai-models/ai-model-list')),
  department_master: lazy(() => import('../departments/department-list')),
  designation_master: lazy(() => import('../designation-master/designation-master-list')),
  district_master: lazy(() => import('../districts/districts-list')),
  error_master: lazy(() => import('../error-master/error-master-list')),
  eval_case: lazy(() => import('../eval-cases/eval-case-list')),
  eval_suite: lazy(() => import('../evals/eval-list')),
  kb_master: lazy(() => import('../knowledge-base/knowledge-base-list')),
  log_master: lazy(() => import('../log-master/log-master-list')),
  ministry_master: lazy(() => import('../ministries/ministry-list')),
  partner_connections: lazy(() => import('../partners/partner-connection-list')),
  partner_master: lazy(() => import('../partners/partner-list')),
  personnel_master: lazy(() => import('../personnel/personnel-list')),
  pipeline_master: lazy(() => import('../pipelines/pipeline-list')),
  prompt_master: lazy(() => import('../prompts/prompt-list')),
  rag_master: lazy(() => import('../rags/rag-list')),
  unit_master: lazy(() => import('../units/units-list')),
  unit_type_master: lazy(() => import('../unit-types/unit-type-list')),
  unit_villages_master: lazy(() => import('../unit-villages/unit-villages-list')),
  user_master: lazy(() => import('../user-management/user-management-list')),
  value_sets_master: lazy(() => import('../value-sets/value-sets-list')),
};
